import React from "react";

import "./ui.css";

const FeedbackIcon = ({ variant }) => {
  if (variant === "error") {
    return (
      <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
        <path d="M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20Zm0 14.75a1.15 1.15 0 1 1 0 2.3 1.15 1.15 0 0 1 0-2.3Zm1-2.75h-2V6h2v8Z" />
      </svg>
    );
  }

  if (variant === "success") {
    return (
      <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
        <path d="M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20Zm-1.25 14.2-4.1-4.1 1.4-1.4 2.7 2.7 5.7-5.7 1.4 1.4-7.1 7.1Z" />
      </svg>
    );
  }

  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path d="M4 4h16a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2Zm0 4v10h16V8l-8 5-8-5Zm1.4-2L12 10.1 18.6 6H5.4Z" />
    </svg>
  );
};

const allowedVariants = ["empty", "error", "success", "info"];

/** Consistent empty, error, success, and informational content state. */
const FeedbackState = ({
  variant = "empty",
  icon,
  title,
  description,
  action,
  children,
  compact = false,
  className = "",
  role,
  ariaLive,
}) => {
  const resolvedVariant = allowedVariants.includes(variant) ? variant : "info";
  const resolvedRole = role || (resolvedVariant === "error" ? "alert" : "status");
  const classes = [
    "rg-ui-feedback-state",
    `rg-ui-feedback-state--${resolvedVariant}`,
    compact && "rg-ui-feedback-state--compact",
    className,
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <section
      className={classes}
      role={resolvedRole}
      aria-live={ariaLive || (resolvedVariant === "error" ? "assertive" : "polite")}
    >
      <div className="rg-ui-feedback-state__icon">
        {icon || <FeedbackIcon variant={resolvedVariant} />}
      </div>
      {title && <h2 className="rg-ui-feedback-state__title">{title}</h2>}
      {description && <div className="rg-ui-feedback-state__description">{description}</div>}
      {children && <div className="rg-ui-feedback-state__content">{children}</div>}
      {action && <div className="rg-ui-feedback-state__action">{action}</div>}
    </section>
  );
};

const EmptyState = ({
  title = "Nothing here yet",
  description,
  action,
  icon,
  ...props
}) => (
  <FeedbackState
    {...props}
    variant="empty"
    icon={icon}
    title={title}
    description={description}
    action={action}
  />
);

const ErrorState = ({
  title = "Something went wrong",
  description = "Please try again.",
  action,
  onRetry,
  retryLabel = "Try again",
  icon,
  ...props
}) => {
  const resolvedAction =
    action ||
    (onRetry ? (
      <button type="button" className="rg-ui-feedback-state__button" onClick={onRetry}>
        {retryLabel}
      </button>
    ) : null);

  return (
    <FeedbackState
      {...props}
      variant="error"
      icon={icon}
      title={title}
      description={description}
      action={resolvedAction}
    />
  );
};

const LoadingSkeleton = ({
  count = 4,
  widths = ["92%", "76%", "84%", "64%"],
  showAvatar = false,
  compact = false,
  label = "Loading content",
  className = "",
}) => {
  const lineCount = Math.max(1, Math.min(12, Number(count) || 4));
  const classes = [
    "rg-ui-loading-skeleton",
    compact && "rg-ui-loading-skeleton--compact",
    className,
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div className={classes} role="status" aria-busy="true" aria-label={label}>
      {showAvatar && <span className="rg-ui-loading-skeleton__avatar" aria-hidden="true" />}
      <div className="rg-ui-loading-skeleton__lines" aria-hidden="true">
        {Array.from({ length: lineCount }).map((_, index) => (
          <span
            className="rg-ui-loading-skeleton__line"
            style={{ width: widths[index % widths.length] || "80%" }}
            key={index}
          />
        ))}
      </div>
      <span className="rg-ui-visually-hidden">{label}</span>
    </div>
  );
};

export { EmptyState, ErrorState, LoadingSkeleton };
export default FeedbackState;
